import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { Plus, Edit, Trash2 } from 'lucide-react';
import DataTable from 'react-data-table-component';

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '', email: '', password: '', tipo_usuario_id: ''
    });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        try {
            const [uRes, rRes] = await Promise.all([
                fetch(`${API_URL}/api/users`, { headers }),
                fetch(`${API_URL}/api/users/roles`, { headers })
            ]);

            setUsers(await uRes.json());
            setRoles(await rRes.json());
            setLoading(false);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const url = editingId
            ? `${API_URL}/api/users/${editingId}`
            : `${API_URL}/api/users`;
        const method = editingId ? 'PUT' : 'POST';

        // Filter out password if empty/editing
        const body = { ...formData };
        if (editingId && !body.password) delete body.password;

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(body)
            });
            if (res.ok) {
                setModalOpen(false);
                setFormData({ name: '', email: '', password: '', tipo_usuario_id: '' });
                setEditingId(null);
                fetchData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id, name) => {
        if (!confirm(`¿Estás seguro de eliminar al usuario "${name}"? Esta acción no se puede deshacer.`)) return;
        try {
            const res = await fetch(`${API_URL}/api/users/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                fetchData();
            } else {
                const data = await res.json();
                alert(data.error || 'Error al eliminar usuario');
            }
        } catch (err) {
            console.error(err);
            alert('Error al eliminar usuario');
        }
    };

    const openEdit = (user) => {
        setFormData({
            name: user.name,
            email: user.email,
            tipo_usuario_id: user.tipo_usuario_id,
            estado: user.estado,
            password: '' // Don't show password
        });
        setEditingId(user.id);
        setModalOpen(true);
    };

    return (
        <div className="p-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Gestión de Usuarios</h1>
                <button
                    onClick={() => { setEditingId(null); setFormData({ name: '', email: '', password: '', tipo_usuario_id: '' }); setModalOpen(true); }}
                    className="btn-primary flex items-center gap-2"
                >
                    <Plus size={18} /> Nuevo Usuario
                </button>
            </div>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <DataTable
                        columns={[
                            { name: 'ID', selector: row => row.id, sortable: true, width: '80px' },
                            { name: 'Nombre', selector: row => row.name, sortable: true },
                            { name: 'Email', selector: row => row.email, sortable: true },
                            {
                                name: 'Rol',
                                selector: row => row.role_name || row.tipo_usuario_id,
                                sortable: true,
                                cell: row => (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                        {row.role_name || row.tipo_usuario_id}
                                    </span>
                                )
                            },
                            {
                                name: 'Estado',
                                selector: row => row.estado,
                                sortable: true,
                                cell: row => (
                                    <span className={`px-2 py-1 rounded text-xs ${row.estado ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {row.estado ? 'Activo' : 'Inactivo'}
                                    </span>
                                )
                            },
                            {
                                name: 'Acciones',
                                cell: row => (
                                    <div className="flex gap-1">
                                        <button onClick={() => openEdit(row)} className="text-blue-600 hover:text-blue-800 p-1" title="Editar">
                                            <Edit size={18} />
                                        </button>
                                        <button onClick={() => handleDelete(row.id, row.name)} className="text-red-600 hover:text-red-800 p-1" title="Eliminar">
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                ),
                                ignoreRowClick: true,
                                allowOverflow: true,
                                button: true,
                                width: '100px'
                            },
                        ]}
                        data={users}
                        pagination
                        highlightOnHover
                        pointerOnHover
                        responsive
                        customStyles={{
                            headRow: {
                                style: {
                                    backgroundColor: '#f9fafb',
                                    borderBottom: '1px solid #e5e7eb',
                                    fontWeight: '600',
                                    color: '#4b5563',
                                },
                            },
                            cells: {
                                style: {
                                    paddingLeft: '16px',
                                    paddingRight: '16px',
                                },
                            },
                        }}
                    />
                </div>
            )}

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="modal-title">{editingId ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
                        <form onSubmit={handleSubmit} className="form-grid">
                            <div className="form-group full-width">
                                <label>Nombre</label>
                                <input
                                    required
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Email</label>
                                <input
                                    required type="email"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                            {!editingId && (
                                <div className="form-group full-width">
                                    <label>Contraseña</label>
                                    <input
                                        required type="password"
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    />
                                </div>
                            )}
                            <div className="form-group full-width">
                                <label>Rol</label>
                                <select
                                    required
                                    className="w-full border rounded p-2"
                                    value={formData.tipo_usuario_id}
                                    onChange={e => setFormData({ ...formData, tipo_usuario_id: e.target.value })}
                                    style={{
                                        backgroundColor: 'var(--bg-input)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: 'var(--radius-md)',
                                        padding: '0.625rem 0.875rem',
                                        fontSize: '0.875rem',
                                        color: 'var(--text-main)'
                                    }}
                                >
                                    <option value="">Seleccionar Rol</option>
                                    {roles.map(r => (
                                        <option key={r.id} value={r.id}>{r.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
